import {
    Component,
    OnInit,
    Inject,
    OnChanges,
    SimpleChanges,
    DestroyRef,
    inject
} from '@angular/core';
import {FormControl, FormGroup, FormBuilder} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {BehaviorSubject, of, throwError} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, tap} from 'rxjs/operators';
import {TreeNode} from '@ali-hm/angular-tree-component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MenuKeyContext} from '@emm/core/models/menu-key.model';
import {LocationSet} from '@emm/core/models/location-set.model';
import {ItemService} from '@emm/core/services/item.service';
import {MenuService} from '@emm/core/services/menu.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {MenuTreeFacadeService} from '@emm/menu/services/menu-tree-facade.service';
import {MenuApiService} from '@emm/menu/services/menu-api.service';
import {API_ERRORS} from '@emm/core/util/api-constants';
import {FlagKeys, LaunchDarklyService} from '@emm/core/services/launch-darkly.service';
import {getCascadeMatchPath, CascadeChip, MatchPathResult} from '@emm/core/util/cascade-matchpath.util';

interface ChipState {
    name: string;
    selected: boolean;
    tag: string;
}

@Component({
    selector: 'emm-delete-child-item-dialog',
    styleUrls: ['./delete-child-item-dialog.component.scss'],
    templateUrl: './delete-child-item-dialog.component.html',
    standalone: false
})
export class DeleteChildItemDialogComponent implements OnInit, OnChanges {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    childDeletePath;
    color = '#e5173f';
    deleteItemToggle = false;
    diameter = 25;
    featureScope: string;
    firstFormGroup: FormGroup;
    input: FormControl = new FormControl();
    isLinear = true;
    menuKey: MenuKeyContext;
    mode = 'indeterminate';
    options: LocationSet[];
    parent: TreeNode;
    parentDeletePath;
    searched: boolean;
    secondFormGroup: FormGroup;
    selectedChild;
    subMenuOnly: FormControl = new FormControl();
    submenus;
    currentSubmenu;
    selectedSubmenus: string[] = [];

    filterValue = '';
    filterSize: number;

    subMenu;

    // Reactive state management
    topChips$ = new BehaviorSubject<ChipState[]>([]);
    bottomChips$ = new BehaviorSubject<ChipState[]>([]);
    matchCount$ = new BehaviorSubject<number>(0);
    childCount$ = new BehaviorSubject<number>(0);
    parentCount$ = new BehaviorSubject<number>(0);
    spinner$ = new BehaviorSubject<boolean>(false);
    path$ = new BehaviorSubject<object | string | null>(null);
    showPath$ = new BehaviorSubject<boolean>(false);

    useNewCascadeMatchPaths$ = this.launchDarklyService.getBooleanFlag(FlagKeys.USE_NEW_CASCADE_MATCH_PATHS);
    useNewCascadeMatchPaths = false;

    // Computed observables
    selectedBottomChips$ = this.bottomChips$.pipe(
        map(chips => chips.filter(chip => chip.selected))
    );


    constructor(
        public dialogRef: MatDialogRef<DeleteChildItemDialogComponent>,
        private formBuilder: FormBuilder,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private itemService: ItemService,
        private menuService: MenuService,
        private notificationService: NotificationService,
        private treeFacade: MenuTreeFacadeService,
        private api: MenuApiService,
        private launchDarklyService: LaunchDarklyService,
    ) {
    }


    ngOnInit(): void {
        this.featureScope = this.data.featureScope;
        this.parent = this.data.parent;
        this.menuKey = this.data.menuKey;
        this.treeFacade.resetCascadeSubmenus();

        this.useNewCascadeMatchPaths$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(value => this.useNewCascadeMatchPaths = value);

        this.input.valueChanges
            .pipe(
                filter((value: any) => typeof value === 'string'),
                switchMap((value) => {
                    if (!value || value.length < 2) {
                        this.options = [];
                        return of(null);
                    }
                    return this.itemService
                        .filterItems(value, null, 25)
                        .pipe(
                            tap((results) => {
                                const exactMatch = results.filter((i) => i.tag === value);
                                if (exactMatch.length > 0) {
                                    this.options = exactMatch;
                                } else {
                                    this.options = results;
                                }
                            }),
                            takeUntilDestroyed(this.destroyRef)
                        );
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        this.firstFormGroup = this.formBuilder.group({
            subMenuOnly: new FormControl('')
        });
        this.secondFormGroup = this.formBuilder.group({
            input: new FormControl('')
        });

        let allPath;
        if (this.featureScope === 'Global' || this.featureScope === 'Viewer') {
            allPath = this.menuService.getParentCascadePathForAll(this.parent.data.data.tagPath, this.featureScope);
        } else {
            allPath = this.menuService.getParentCascadePathForAll(this.parent.data.data.tag, this.featureScope);
        }

        this.findParents(this.featureScope, allPath)
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
        this.subMenuOnly.valueChanges
            .pipe(
                tap(() => {
                    this.input.setValue(null);
                    this.searched = true;
                    this.selectedChild = null;
                    this.childCount$.next(0);
                }),
                map((value: any) => {
                    let path: string = allPath;
                    if (value === true) {
                        const subMenu = this.findSubMenuParent(this.parent, null);
                        path = this.menuService.getParentCascadePathForSubmenu(
                            subMenu.data.data.jsonPath,
                            this.parent.data.data.tag,
                            this.featureScope
                        );
                    }
                    return path;
                }),
                switchMap((path) => this.findParents(this.featureScope, path)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        // React to top chip selection changes
        this.topChips$
            .pipe(
                debounceTime(50),
                distinctUntilChanged((prev, curr) =>
                    JSON.stringify(prev) === JSON.stringify(curr)
                ),
                tap(() => {
                    const currentChips = this.topChips$.value;
                    this.subMenu = currentChips.filter((ch) => ch.tag !== 'ROOT').find((ch) => ch.selected);
                    this.recalculatePath();
                }),
                switchMap(() => this.findMatches(this.featureScope, this.path$.value)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        // React to bottom chip selection changes
        this.bottomChips$
            .pipe(
                debounceTime(50),
                distinctUntilChanged((prev, curr) =>
                    JSON.stringify(prev) === JSON.stringify(curr)
                ),
                tap(() => this.recalculatePath()),
                switchMap(() => this.findMatches(this.featureScope, this.path$.value)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        this.setSubmenus();
        this.initChips();
        this.recalculatePath();
    }


    ngOnChanges(changes: SimpleChanges) {
        this.refreshCascadeSubmenus();
    }

    private findSubMenuParent(parent: TreeNode, previousParent: TreeNode): TreeNode {
        const nextParent = parent.parent;

        // submenu is the last previous parent before reaching root which has no more parent
        if (nextParent === null) {
            return previousParent;
        }

        return this.findSubMenuParent(nextParent, parent);
    }


    refreshCascadeSubmenus() {
        this.treeFacade.selectedSubmenusDelete$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(result => {
                this.selectedSubmenus = [...result].filter(submenu => submenu !== null && submenu !== undefined);
            });
    }


    deselectCascadeSubmenus(submenuTag: string) {
        if (this.selectedSubmenus.includes(submenuTag)) {
            this.selectedSubmenus.splice(this.selectedSubmenus.indexOf((submenuTag)));
        }
    }


    displayFn(option): string | undefined {
        return option ? option.name : undefined;
    }


    onSave(): void {
        const result = {
            item: this.parent.data.data.value,
            childPath: this.parentDeletePath
        };
        of(this.treeFacade.deleteItemCascade(result.childPath, result.item))
            .pipe(
                tap((_) => {
                    this.notificationService.success('Deleted Successfully');
                }),
                tap(() => this.dialogRef.close(result)),
                switchMap(() => this.treeFacade.reloadItems()),
                takeUntilDestroyed(this.destroyRef)
            ).subscribe({
            error: (error) => {
                this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
            }
        });
    }


    onNoClick(): void {
        this.dialogRef.close();
    }


    onChange(): void {
        this.selectedChild = null;
    }


    onSelect(e: MouseEvent): void {
        this.searched = true;
        this.spinner$.next(true);
        this.deleteItemToggle = false;
        this.selectedChild = this.parent.data.data;
        const path = this.menuService.getDeleteCascadePath(this.parentDeletePath, this.selectedChild.tag, this.featureScope);
        this.refreshCascadeSubmenus();
        if (this.featureScope === 'Global' || this.featureScope === 'Viewer') {
            this.menuService
                .countMatchesDelete(this.featureScope, this.data.effectiveDate, this.menuKey, path,
                    this.selectedSubmenus.length === 0 ? [this.currentSubmenu] : this.selectedSubmenus)
                .pipe(
                    catchError(() => {
                        const errorMessage = 'Error searching child nodes for parent';
                        this.notificationService.error(errorMessage);
                        return throwError(errorMessage);
                    }),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe((results) => {
                    this.spinner$.next(false);
                    this.childCount$.next(results);
                    if (results > 0) {
                        this.deleteItemToggle = true;
                        this.childDeletePath = path;
                    }
                });
        }
    }

    findParents(featureScope: string, path: string) {
        this.parentDeletePath = path;
        this.spinner$.next(true);
        const pathArray = path.split('/');
        this.currentSubmenu = pathArray[1];

        if (this.featureScope === 'Global' || this.featureScope === 'Viewer') {
            const source$ = this.menuService
                .countMatchesDelete(featureScope, this.data.effectiveDate, this.menuKey, path, this.selectedSubmenus)
                .pipe(
                    tap((results) => {
                        this.searched = true;
                        this.spinner$.next(false);
                        this.parentCount$.next(results);
                        this.matchCount$.next(results);
                    }),
                    catchError(() => {
                        const errorMessage = 'Error searching parent nodes';
                        this.notificationService.error(errorMessage);
                        this.spinner$.next(false);
                        return throwError(errorMessage);
                    })
                );

            return source$;
        }
        return of(null);
    }

    updateAllSelected(checked: boolean, checkedValue: string): void {
        if (!(this.selectedSubmenus.includes(this.currentSubmenu))) {
            this.treeFacade.selectCascadeSubmenus(this.currentSubmenu);
        }

        this.spinner$.next(true);
        if (checked) {
            this.treeFacade.selectCascadeSubmenus(checkedValue);
            this.refreshCascadeSubmenus();
        } else {
            this.treeFacade.deselectCascadeSubmenus(checkedValue);
            this.deselectCascadeSubmenus(checkedValue);
        }

        if (this.featureScope === 'Global' || this.featureScope === 'Viewer') {
            this.menuService.countMatchesDelete(
                this.featureScope,
                this.data.effectiveDate,
                this.menuKey,
                this.parentDeletePath,
                this.selectedSubmenus.length === 0 ? [this.currentSubmenu] : this.selectedSubmenus
            )
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe({
                    next: (results) => {
                        this.parentCount$.next(results);
                        this.matchCount$.next(results);
                        this.spinner$.next(false);
                    },
                    error: () => {
                        const errorMessage = 'Error searching parent nodes';
                        this.notificationService.error(errorMessage);
                        this.spinner$.next(false);
                    }
                });
        }
    }

    onFilterChange($event: Event) {
        const filterStr = ($event.target as HTMLInputElement).value;
        this.filterSubmenus(filterStr.toLowerCase());
    }

    private filterSubmenus(value: string): void {
        this.setSubmenus(value?.toLowerCase());
    }

    public clearFilter() {
        this.filterValue = '';
        this.setSubmenus();
    }

    private setSubmenus(filterStr?: string) {
        this.api.getSubmenusByFeatureScope(this.featureScope, this.menuKey)
            .pipe(
                debounceTime(300),
                map((result) => {
                    if (filterStr) {
                        return result.filter((item) => item.name.includes(filterStr)
                            || item.id.toLowerCase().includes(filterStr));
                    } else {
                        return result;
                    }
                }),
                map((submenus) => this.submenus = submenus),
                tap((submenus) => this.filterSize = submenus.length),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    initChips(): void {
        // find item hierarchy
        const nodes: ChipState[] = [];
        nodes.push(this.createChip(this.parent?.data?.data, true));
        this.findChipParents(this.parent).forEach((parent) =>
            nodes.push(this.createChip(parent?.data?.data, false))
        );
        const chips = nodes.reverse();

        // split item path to top-level (root / submenu) and bottom-level (parent ancestors)
        const rootChipName = 'Submenu';
        this.topChips$.next([{
            name: rootChipName,
            selected: true,
            tag: 'ROOT'
        }]);
        const bottomChips = chips.slice(1);
        this.bottomChips$.next(bottomChips);

        // Initial path calculation
        this.recalculatePath();
    }

    createChip({name = '', tag = ''} = {}, selected: boolean): ChipState {
        return {name, selected, tag};
    }

    findChipParents(item: TreeNode): TreeNode[] {
        return this.findChipParent(item, []);
    }

    findChipParent(node: TreeNode, parents: TreeNode[]): TreeNode[] {
        const parent = node?.parent;
        if (parent && !node.isRoot) {
            parents.push(parent);
            return this.findChipParent(parent, parents);
        }
        return parents;
    }

    private recalculatePath(): void {
        const chips = this.bottomChips$.value;
        const selectedBottomChips = chips.filter(chip => chip.selected);
        const path = this.getPath(selectedBottomChips);
        this.path$.next(path);
    }

    getPath(selectedBottomChips: ChipState[]): MatchPathResult {
        return getCascadeMatchPath(
            selectedBottomChips as CascadeChip[],
            this.bottomChips$.value as CascadeChip[],
            this.useNewCascadeMatchPaths
        );
    }

    findMatches(featureScope: string, path: any): any {
        this.spinner$.next(true);

        if (this.featureScope === 'Global' || this.featureScope === 'Viewer') {
            const source$ = this.menuService
                .countMatchesForUpdateCascade(
                    featureScope,
                    this.data.effectiveDate,
                    this.menuKey,
                    path,
                    this.selectedSubmenus.length === 0 ? [this.currentSubmenu] : this.selectedSubmenus
                )
                .pipe(
                    tap((results) => {
                        this.spinner$.next(false);
                        this.matchCount$.next(results);
                    }),
                    catchError(() => {
                        const errorMessage = 'Error searching matches';
                        this.notificationService.error(errorMessage);
                        this.spinner$.next(false);
                        return throwError(errorMessage);
                    })
                );

            return source$;
        }

        this.spinner$.next(false);
        return of(null);
    }

    toggleSelectTopChip(chip: ChipState): void {
        const currentChips = this.topChips$.value;
        const updatedChips = currentChips.map(ch => ({
            ...ch,
            selected: ch.tag === chip.tag ? !ch.selected : !chip.selected
        }));
        this.topChips$.next(updatedChips);
    }

    toggleSelectBottomChip(chip: ChipState): void {
        // Validate deselecting bottom chip (item)
        if (chip.tag === this.data.parent.data.data.tag) {
            this.notificationService.error('Cannot deselect item to be updated');
            return;
        }

        const currentChips = this.bottomChips$.value;
        const chipIndex = currentChips.findIndex(c => c.tag === chip.tag);

        if (chipIndex === -1) return;

        // Toggle selected chip
        const newValue = !chip.selected;
        const updatedChips = [...currentChips];
        updatedChips[chipIndex] = {...updatedChips[chipIndex], selected: newValue};

        // If selecting, set all child descendants to true
        if (newValue) {
            for (let i = chipIndex + 1; i < updatedChips.length; i++) {
                updatedChips[i] = {...updatedChips[i], selected: true};
            }
        }

        // Check if the deselection creates 2 or more gaps
        const selectedIndexes = updatedChips
            .map((ch, index) => (ch.selected ? index : -1))
            .filter(index => index !== -1);

        const sortedIndexes = selectedIndexes.sort((a, b) => a - b);
        const gapCount = sortedIndexes.reduce((count, index, i) => {
            if (i < sortedIndexes.length - 1 && sortedIndexes[i + 1] - index > 1) {
                return count + 1;
            }
            return count;
        }, 0);

        if (gapCount > 1) {
            this.notificationService.error('Cannot create 2 or more gaps in selected chips');
            return;
        }

        // Update state immutably
        this.bottomChips$.next(updatedChips);
    }

    togglePath(): void {
        this.showPath$.next(!this.showPath$.value);
    }


}
